// src/components/LocationSelector.jsx
import React, { useState, useEffect, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import debounce from 'lodash.debounce';

export default function LocationSelector({ values, onChange, errors = {} }) {
  const serviceRef = useRef(null);
  const detailsServiceRef = useRef(null);
  const [cityBounds, setCityBounds] = useState(null);
  const [areaBounds, setAreaBounds] = useState(null);

  // Initialize Google services once
  useEffect(() => {
    if (window.google?.maps?.places) {
      serviceRef.current = new window.google.maps.places.AutocompleteService();
      const div = document.createElement('div');
      detailsServiceRef.current = new window.google.maps.places.PlacesService(div);
    } else {
      console.error('Google Maps Places API not loaded');
    }
  }, []);

  // Debounced predictions loader for performance
  const debouncedGetPredictions = useRef(
    debounce((request, callback) => {
      serviceRef.current.getPlacePredictions(request, callback);
    }, 300)
  ).current;

  const loadOptions = (inputValue, types, bounds) =>
    new Promise(resolve => {
      if (!serviceRef.current) return resolve([]);
      const request = {
        input: inputValue,
        types,
        componentRestrictions: { country: 'in' },
        ...(bounds ? { bounds, strictBounds: true } : {})
      };
      debouncedGetPredictions(request, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(predictions.map(p => ({ label: p.description, value: p.place_id })));
        } else {
          resolve([]);
        }
      });
    });

  const handleSelect = (option, name, childClear, setBounds) => {
    if (!option) {
      onChange({ target: { name, value: '' } });
      childClear && onChange({ target: { name: childClear, value: '' } });
      return;
    }
    detailsServiceRef.current.getDetails(
      { placeId: option.value, fields: ['address_components', 'geometry'] },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK) return;
        const comps = place.address_components || [];
        let value = '';
        if (name === 'province') {
          value = comps.find(c => c.types.includes('administrative_area_level_1'))?.long_name || '';
        } else if (name === 'city') {
          value = comps.find(c => c.types.includes('locality') || c.types.includes('postal_town'))?.long_name || '';
        } else {
          value = comps.find(c => c.types.includes('sublocality') || c.types.includes('neighborhood'))?.long_name || '';
        }
        onChange({ target: { name, value } });
        childClear && onChange({ target: { name: childClear, value: '' } });
        if (place.geometry?.viewport && setBounds) setBounds(place.geometry.viewport);
      }
    );
  };

  const commonAsyncProps = {
    cacheOptions: true,
    defaultOptions: [],
    isClearable: true,
    noOptionsMessage: ({ inputValue }) =>
      inputValue.length < 2 ? 'Type at least 2 characters' : 'No results',
    loadingMessage: () => 'Loading…',
    styles: {
      menuPortal: base => ({ ...base, zIndex: 9999 }),
      control: base => ({ ...base, minHeight: '40px' }),
      placeholder: base => ({ ...base, color: '#666' })
    },
    menuPortalTarget: document.body
  };

  return (
    <>
      {/* Province / State */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">
          Province / State<span className="text-red-500 ml-1">*</span>
        </label>
        <AsyncSelect
          {...commonAsyncProps}
          loadOptions={input => loadOptions(input, ['administrative_area_level_1'])}
          onChange={opt => handleSelect(opt, 'province', 'city', setCityBounds)}
          value={values.province ? { label: values.province, value: values.province } : null}
          placeholder="Search or select a state"
        />
        {errors.province && <p className="text-red-500 text-xs mt-1">{errors.province}</p>}
      </div>

      {/* City */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">
          City<span className="text-red-500 ml-1">*</span>
        </label>
        <AsyncSelect
          {...commonAsyncProps}
          isDisabled={!values.province}
          loadOptions={input => loadOptions(input, ['locality', 'postal_town'], cityBounds)}
          onChange={opt => handleSelect(opt, 'city', 'area', setAreaBounds)}
          value={values.city ? { label: values.city, value: values.city } : null}
          placeholder={values.province ? 'Search or select a city' : 'Select state first'}
        />
        {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
      </div>

      {/* Area / Locality */}
      <div className="mb-4">
        <label className="block mb-1 font-medium">
          Area / Locality<span className="text-red-500 ml-1">*</span>
        </label>
        <AsyncSelect
          {...commonAsyncProps}
          isDisabled={!values.city}
          loadOptions={input => loadOptions(input, ['sublocality', 'neighborhood'], areaBounds)}
          onChange={opt => handleSelect(opt, 'area')}
          value={values.area ? { label: values.area, value: values.area } : null}
          placeholder={values.city ? 'Search or select an area' : 'Select city first'}
        />
        {errors.area && <p className="text-red-500 text-xs mt-1">{errors.area}</p>}
      </div>
    </>
  );
}
